import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Database, 
  GitBranch, 
  Settings, 
  Eye, 
  Copy, 
  Edit3,
  AlertCircle,
  CheckCircle,
  Info,
  RefreshCw,
  Filter,
  Download,
  Upload,
  Plus,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Bot
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import DataVisualization from "./DataVisualization";
import EntityRelationships from "./EntityRelationships";
import JsonViewer from "./JsonViewer";
import EntityDetailModal from "./EntityDetailModal";
import EntityCloneModal from "./EntityCloneModal";
import EntityCompareModal from "./EntityCompareModal";
import ConnectorPairBuilder from "./ConnectorPairBuilder";
import AIModuleCreator from "./AIModuleCreator";
import DatabaseConfigEditor from "./DatabaseConfigEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  // Suppress ResizeObserver errors globally
  React.useEffect(() => {
    const handleResizeObserverError = (event) => {
      if (event.message === 'ResizeObserver loop completed with undelivered notifications.' || 
          event.message.includes('ResizeObserver loop')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [migrationEntities, setMigrationEntities] = useState([]);
  const [migrationSources, setMigrationSources] = useState([]);
  const [migrationTargets, setMigrationTargets] = useState([]);
  const [sourceFilterOptions, setSourceFilterOptions] = useState([]);
  const [targetConnectionFields, setTargetConnectionFields] = useState([]);
  const [dataRelationships, setDataRelationships] = useState([]);
  const [sourceTargetPairs, setSourceTargetPairs] = useState([]);

  // Memoize sourceTargetPairs to prevent unnecessary re-renders
  const memoizedSourceTargetPairs = React.useMemo(() => {
    return sourceTargetPairs || [];
  }, [sourceTargetPairs]);
  
  const [selectedSourceTarget, setSelectedSourceTarget] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [dbConnectionStatus, setDbConnectionStatus] = useState("unknown");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showConnectorBuilder, setShowConnectorBuilder] = useState(false);
  const [showAIModuleCreator, setShowAIModuleCreator] = useState(false);
  const [showDatabaseConfigEditor, setShowDatabaseConfigEditor] = useState(false);
  const [expandedEntities, setExpandedEntities] = useState(new Set());
  const [selectedDatabase, setSelectedDatabase] = useState(() => {
    // Initialize from localStorage or default to 'data_migration'
    return localStorage.getItem('selectedDatabase') || 'data_migration';
  });
  const [isSwitchingDatabase, setIsSwitchingDatabase] = useState(false);

  const handleViewEntity = (entity) => {
    setSelectedEntity(entity);
    setShowDetailModal(true);
  };

  const handleEditEntity = (entity) => {
    setSelectedEntity(entity);
    setShowDatabaseConfigEditor(true);
  };

  const handleCloneEntityModal = (entity) => {
    setSelectedEntity(entity);
    setShowCloneModal(true);
  };

  const handleSaveEntity = (editedEntity) => {
    // Update entity in the migrationEntities array
    setMigrationEntities(prev => 
      prev.map(entity => 
        entity.id === editedEntity.id ? editedEntity : entity
      )
    );
    setShowEditModal(false);
    setSelectedEntity(null);
    toast.success("Entity updated successfully!");
  };

  const handleSaveConfiguration = async (configData) => {
    try {
      // Call backend API to save configuration
      const response = await axios.post(`${API}/update-configuration`, {
        source: configData.source,
        target: configData.target,
        migration_entities: configData.migration_entities,
        migration_sources: configData.migration_sources,
        migration_targets: configData.migration_targets,
        target_connection_fields: configData.target_connection_fields,
        validate_source_data: configData.validate_source_data,
        source_filter_options: configData.source_filter_options,
        validate_auth: configData.validate_auth
      });
      
      toast.success("Configuration saved successfully!");
      setShowDatabaseConfigEditor(false);
      setSelectedEntity(null);
      
      // Refresh the data to show updated values
      fetchAllData();
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error(error.response?.data?.detail || "Failed to save configuration");
      throw error;
    }
  };

  const handleCloneEntity = (clonedEntity) => {
    // TODO: Implement API call to create new entity
    console.log('Cloning entity:', clonedEntity);
    // For now, add to local state with new ID
    const newEntity = {
      ...clonedEntity,
      id: Math.max(...migrationEntities.map(e => e.id)) + 1
    };
    setMigrationEntities(prev => [...prev, newEntity]);
  };

  const toggleEntityExpanded = (entityId) => {
    const newExpanded = new Set(expandedEntities);
    if (newExpanded.has(entityId)) {
      newExpanded.delete(entityId);
    } else {
      newExpanded.add(entityId);
    }
    setExpandedEntities(newExpanded);
  };

  const getTargetNameById = (targetId) => {
    const target = migrationTargets.find(t => t.id === targetId);
    return target ? `${target.name} (ID: ${targetId})` : `ID: ${targetId}`;
  };

  const getSourceNameById = (sourceId) => {
    const source = migrationSources.find(s => s.id === sourceId);
    return source ? `${source.provider || source.source} (ID: ${sourceId})` : `ID: ${sourceId}`;
  };

  const renderJsonPreview = (data, maxLines = 3) => {
    if (!data) return <span className="text-slate-400">No data</span>;
    
    const jsonString = JSON.stringify(data, null, 2);
    return (
      <div className="bg-slate-50 border rounded p-2 font-mono text-xs max-h-32 overflow-auto">
        <pre className="text-slate-700 whitespace-pre-wrap">{jsonString}</pre>
      </div>
    );
  };

  const getEntityName = (entity) => {
    // Try to get entity name from sourceField first
    if (entity.sourceField && entity.sourceField.length > 0) {
      return entity.sourceField[0].entity || 'N/A';
    }
    // Fallback to entity field
    return entity.entity || 'N/A';
  };

  useEffect(() => {
    // Load current database from backend on mount
    loadCurrentDatabase();
    testDatabaseConnection();
    fetchAllData();
  }, []);
  
  const loadCurrentDatabase = async () => {
    try {
      const response = await axios.get(`${API}/current-database`);
      const backendDatabase = response.data.current_database;
      
      // If localStorage has a different value, sync backend with localStorage preference
      const localStorageDb = localStorage.getItem('selectedDatabase');
      if (localStorageDb && localStorageDb !== backendDatabase) {
        await switchDatabase(localStorageDb, false); // false = don't show toast on initial load
      } else {
        setSelectedDatabase(backendDatabase);
      }
    } catch (error) {
      console.error("Error loading current database:", error);
      // If backend call fails, stick with localStorage value
    }
  };
  
  const switchDatabase = async (databaseName, showToast = true) => {
    setIsSwitchingDatabase(true);
    try {
      // Call backend to switch database
      await axios.post(`${API}/switch-database`, { database: databaseName });
      
      // Update state and localStorage
      setSelectedDatabase(databaseName);
      localStorage.setItem('selectedDatabase', databaseName);
      
      if (showToast) {
        const dbLabel = databaseName === 'data_migration' ? 'Production (PROD)' : 'Development (DEV)';
        toast.success(`Switched to ${dbLabel} database`);
      }
      
      // Refresh data with new database
      await fetchAllData();
    } catch (error) {
      console.error("Error switching database:", error);
      toast.error(error.response?.data?.detail || "Failed to switch database");
    } finally {
      setIsSwitchingDatabase(false);
    }
  };
  
  const handleDatabaseChange = (newDatabase) => {
    if (newDatabase !== selectedDatabase) {
      switchDatabase(newDatabase, true);
    }
  };

  const testDatabaseConnection = async () => {
    try {
      const response = await axios.get(`${API}/test-db`);
      setDbConnectionStatus("connected");
      // Don't show toast on initial connection test
      // toast.success("Database connected successfully");
    } catch (error) {
      setDbConnectionStatus("error");
      toast.error("Database connection failed: " + error.message);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        entitiesRes,
        sourcesRes,
        targetsRes,
        sourceFilterRes,
        targetFieldsRes,
        relationshipsRes,
        pairsRes
      ] = await Promise.all([
        axios.get(`${API}/migration-entities`),
        axios.get(`${API}/migration-sources`),
        axios.get(`${API}/migration-targets`),
        axios.get(`${API}/source-filter-options`),
        axios.get(`${API}/target-connection-fields`),
        axios.get(`${API}/data-relationships`),
        axios.get(`${API}/source-target-pairs`)
      ]);

      setMigrationEntities(entitiesRes.data);
      setMigrationSources(sourcesRes.data);
      setMigrationTargets(targetsRes.data);
      setSourceFilterOptions(sourceFilterRes.data);
      setTargetConnectionFields(targetFieldsRes.data);
      setDataRelationships(relationshipsRes.data);
      setSourceTargetPairs(pairsRes.data);

      toast.success("Data loaded successfully");
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getConnectionStatusBadge = () => {
    switch (dbConnectionStatus) {
      case "connected":
        return <Badge className="badge-success"><CheckCircle className="w-3 h-3 mr-1" />Connected</Badge>;
      case "error":
        return <Badge className="badge-error"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge className="badge-warning"><Info className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  const filteredEntities = migrationEntities.filter(entity => {
    const entityName = getEntityName(entity);
    const matchesSearch = !searchTerm || 
      entity.source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.target?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entityName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedSourceTarget === "all" || 
      `${entity.source}-${entity.target}` === selectedSourceTarget;
    
    return matchesSearch && matchesFilter;
  });

  const getStats = () => {
    return {
      totalEntities: migrationEntities.length,
      totalSources: migrationSources.length,
      totalTargets: migrationTargets.length,
      uniqueSourceSystems: [...new Set(migrationEntities.map(e => e.source))].length,
      uniqueTargetSystems: [...new Set(migrationEntities.map(e => e.target))].length,
      totalPairs: sourceTargetPairs.length
    };
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 mb-1">
                migrateGenie AI Config Tool
              </h1>
              <p className="text-sm text-slate-600">
                Visualize and manage your data migration configurations
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Connection Status */}
              <div className="flex items-center">
                {getConnectionStatusBadge()}
              </div>
              
              {/* Database Selector with Badge */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Database:</label>
                <div className="flex items-center gap-2">
                  <select 
                    value={selectedDatabase}
                    onChange={(e) => handleDatabaseChange(e.target.value)}
                    disabled={isSwitchingDatabase || loading}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="data_migration">data_migration</option>
                    <option value="data_migration_dev">data_migration_dev</option>
                  </select>
                  {/* Environment Badge */}
                  {selectedDatabase === 'data_migration' ? (
                    <Badge className="bg-green-600 text-white border-green-700 px-2 py-1">
                      PROD
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-500 text-white border-yellow-600 px-2 py-1">
                      DEV
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <Button 
                  onClick={fetchAllData} 
                  disabled={loading || isSwitchingDatabase} 
                  variant="outline"
                  size="sm"
                  className="hover:bg-slate-50"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading || isSwitchingDatabase ? 'animate-spin' : ''}`} />
                  Refresh Data
                </Button>
                
                <Button 
                  onClick={() => setShowConnectorBuilder(true)} 
                  size="sm"
                  className="bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Build Connector Pair
                </Button>
                
                <Button 
                  onClick={() => setShowAIModuleCreator(true)} 
                  size="sm"
                  className="bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  <Bot className="w-4 h-4 mr-2" />
                  AI Create Module
                </Button>
                
                <Button 
                  onClick={() => setShowCompareModal(true)} 
                  variant="outline"
                  size="sm"
                  className="hover:bg-slate-50"
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Compare Entities
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Database className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalEntities}</p>
                  <p className="text-xs text-slate-600">Entities</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Upload className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalSources}</p>
                  <p className="text-xs text-slate-600">Sources</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Download className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalTargets}</p>
                  <p className="text-xs text-slate-600">Targets</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <GitBranch className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.uniqueSourceSystems}</p>
                  <p className="text-xs text-slate-600">Source Systems</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Settings className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.uniqueTargetSystems}</p>
                  <p className="text-xs text-slate-600">Target Systems</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <GitBranch className="w-5 h-5 text-teal-600" />
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalPairs}</p>
                  <p className="text-xs text-slate-600">Pairs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="visualization">Visualization</TabsTrigger>
            <TabsTrigger value="data-explorer">Data Explorer</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Filters */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Filter className="w-5 h-5" />
                  <span>Filters</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Search
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by source, target, or entity..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Source-Target Pair
                    </label>
                    <Select 
                      value={selectedSourceTarget} 
                      onValueChange={setSelectedSourceTarget}
                      onOpenChange={() => {
                        // Suppress ResizeObserver errors
                        const handleResizeObserverError = (event) => {
                          if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
                            event.stopImmediatePropagation();
                          }
                        };
                        window.addEventListener('error', handleResizeObserverError);
                        setTimeout(() => {
                          window.removeEventListener('error', handleResizeObserverError);
                        }, 100);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source-target pair" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        <SelectItem value="all">All Pairs</SelectItem>
                        {memoizedSourceTargetPairs.map((pair, index) => (
                          <SelectItem 
                            key={`${pair.source}-${pair.target}-${index}`} 
                            value={`${pair.source}-${pair.target}`}
                          >
                            {pair.source} → {pair.target}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Migration Entities Table */}
            <Card className="card">
              <CardHeader>
                <CardTitle>Migration Entities</CardTitle>
                <CardDescription>
                  Showing {filteredEntities.length} of {migrationEntities.length} entities
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded loading-skeleton"></div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredEntities.map((entity) => {
                      const isExpanded = expandedEntities.has(entity.id);
                      const hasIncompatibles = entity.incompatible && entity.incompatible.length > 0;
                      const hasDependencies = entity.dependentTargets && entity.dependentTargets.length > 0;
                      
                      return (
                        <div key={entity.id} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <button
                                onClick={() => toggleEntityExpanded(entity.id)}
                                className="p-1 hover:bg-slate-200 rounded"
                              >
                                {isExpanded ? 
                                  <ChevronDown className="w-4 h-4 text-slate-600" /> : 
                                  <ChevronRight className="w-4 h-4 text-slate-600" />
                                }
                              </button>
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <Database className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-800 flex items-center space-x-2">
                                  <span>{entity.source}</span>
                                  <ArrowRight className="w-4 h-4 text-slate-400" />
                                  <span>{entity.target}</span>
                                </h3>
                                <div className="flex items-center space-x-2 mt-1">
                                  <p className="text-sm text-slate-600">
                                    Entity: {getEntityName(entity)} | ID: {entity.id}
                                  </p>
                                  {hasIncompatibles && (
                                    <Badge className="badge-error">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Incompatible
                                    </Badge>
                                  )}
                                  {hasDependencies && (
                                    <Badge className="badge-warning">
                                      <GitBranch className="w-3 h-3 mr-1" />
                                      {entity.dependentTargets.length} Dependencies
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button size="sm" variant="outline" onClick={() => handleViewEntity(entity)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleEditEntity(entity)}>
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCloneEntityModal(entity)}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="mt-4 pt-4 border-t space-y-4">
                              {/* Basic Configuration */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="font-medium text-slate-800 mb-2">Configuration</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Migration Object ID:</span>
                                      <span className="font-mono">{entity.migrationObject || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Main Target ID:</span>
                                      <span className="font-mono">{entity.mainTarget || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Custom Field Key:</span>
                                      <span className="font-mono">{entity.customFieldKey || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-slate-600">Delimiter Enabled:</span>
                                      {entity.isDelimiterEnabled ? 
                                        <CheckCircle className="w-4 h-4 text-green-600" /> : 
                                        <span className="text-slate-400">No</span>
                                      }
                                      {entity.delimiter && (
                                        <span className="ml-2 font-mono bg-slate-100 px-1 rounded">
                                          "{entity.delimiter}"
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <h4 className="font-medium text-slate-800 mb-2">Dependencies & Mapping</h4>
                                  <div className="space-y-2">
                                    {entity.dependentTargets && entity.dependentTargets.length > 0 && (
                                      <div>
                                        <span className="text-sm text-slate-600">Dependent Targets:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {entity.dependentTargets.map((depId, index) => (
                                            <Badge key={index} className="badge-warning text-xs">
                                              {getTargetNameById(depId)}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {entity.mappingTargets && entity.mappingTargets.length > 0 && (
                                      <div>
                                        <span className="text-sm text-slate-600">Mapping Targets:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {entity.mappingTargets.map((mapId, index) => (
                                            <Badge key={index} className="badge-info text-xs">
                                              {getTargetNameById(mapId)}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {entity.incompatible && entity.incompatible.length > 0 && (
                                      <div>
                                        <span className="text-sm text-slate-600">Incompatible Items:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {entity.incompatible.map((inc, index) => (
                                            <Badge key={index} className="badge-error text-xs">
                                              {inc}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Source and Target Field Configuration */}
                              {(entity.sourceField || entity.targetField) && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  {entity.sourceField && (
                                    <div>
                                      <h4 className="font-medium text-slate-800 mb-2 flex items-center">
                                        <Database className="w-4 h-4 mr-1" />
                                        Source Field Configuration
                                      </h4>
                                      {renderJsonPreview(entity.sourceField)}
                                    </div>
                                  )}

                                  {entity.targetField && (
                                    <div>
                                      <h4 className="font-medium text-slate-800 mb-2 flex items-center">
                                        <Settings className="w-4 h-4 mr-1" />
                                        Target Field Configuration
                                      </h4>
                                      {renderJsonPreview(entity.targetField)}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Source Extender */}
                              {entity.sourceExtender && (
                                <div>
                                  <h4 className="font-medium text-slate-800 mb-2">Source Extender Configuration</h4>
                                  {renderJsonPreview(entity.sourceExtender)}
                                </div>
                              )}

                              {/* Related Configuration Details */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium text-slate-800 mb-3">Related Configuration Details</h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  {entity.migrationObject && (
                                    <div>
                                      <h5 className="text-sm font-medium text-slate-700 mb-2">
                                        Migration Source (ID: {entity.migrationObject})
                                      </h5>
                                      <div className="p-2 bg-blue-50 rounded border">
                                        <Badge className="badge-info">
                                          {getSourceNameById(entity.migrationObject)}
                                        </Badge>
                                      </div>
                                    </div>
                                  )}

                                  {entity.mainTarget && (
                                    <div>
                                      <h5 className="text-sm font-medium text-slate-700 mb-2">
                                        Main Target (ID: {entity.mainTarget})
                                      </h5>
                                      <div className="p-2 bg-green-50 rounded border">
                                        <Badge className="badge-success">
                                          {getTargetNameById(entity.mainTarget)}
                                        </Badge>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="visualization">
            <DataVisualization 
              entities={migrationEntities}
              sources={migrationSources}
              targets={migrationTargets}
              relationships={dataRelationships}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="data-explorer">
            <JsonViewer 
              entities={migrationEntities}
              sources={migrationSources}
              targets={migrationTargets}
              sourceFilterOptions={sourceFilterOptions}
              targetConnectionFields={targetConnectionFields}
              loading={loading}
            />
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <EntityDetailModal 
          entity={selectedEntity}
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          sources={migrationSources}
          targets={migrationTargets}
        />
        
        
        <EntityCloneModal 
          entity={selectedEntity}
          open={showCloneModal}
          onOpenChange={setShowCloneModal}
          onClone={handleCloneEntity}
          sourceTargetPairs={sourceTargetPairs}
        />
        
        <EntityCompareModal 
          entities={migrationEntities}
          open={showCompareModal}
          onOpenChange={setShowCompareModal}
          sourceTargetPairs={sourceTargetPairs}
        />
        
        <ConnectorPairBuilder 
          open={showConnectorBuilder}
          onOpenChange={setShowConnectorBuilder}
          entities={migrationEntities}
          sources={migrationSources}
          targets={migrationTargets}
        />
        
        <AIModuleCreator 
          open={showAIModuleCreator}
          onOpenChange={setShowAIModuleCreator}
          entities={migrationEntities}
        />
        
        <DatabaseConfigEditor 
          open={showDatabaseConfigEditor}
          onOpenChange={setShowDatabaseConfigEditor}
          entityData={selectedEntity}
          isEditMode={true}
          onSave={handleSaveConfiguration}
          entities={migrationEntities}
          sources={migrationSources}
          targets={migrationTargets}
          sourceFilterOptions={sourceFilterOptions}
          targetConnectionFields={targetConnectionFields}
        />
      </div>
    </div>
  );
};

export default Dashboard;